// Login without a server: your password is turned into a key (PBKDF2, 310k rounds) that
// unlocks a random data key (AES-256-GCM). Everything saved – browser storage, the
// Google Drive file, backups – is encrypted with that data key. Each user has their own
// wrapped copy of the data key, so several people can share one workspace.

const enc = new TextEncoder();
const dec = new TextDecoder();
export const PBKDF2_ITERATIONS = 310000;

function b64(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const norm = (u) => String(u || "").trim().toLowerCase();

export const cryptoSupported = () => typeof crypto !== "undefined" && !!crypto.subtle;

async function passwordKey(username, password, saltB64, iterations) {
  const base = await crypto.subtle.importKey("raw", enc.encode(`${norm(username)}\u0000${password}`), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: unb64(saltB64), iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export function newDataKey() {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
}

// Wrap the (extractable) data key for one user.
export async function makeUser(dataKey, username, password) {
  const salt = b64(crypto.getRandomValues(new Uint8Array(16)));
  const kek = await passwordKey(username, password, salt, PBKDF2_ITERATIONS);
  const raw = await crypto.subtle.exportKey("raw", dataKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, kek, raw);
  return { username: String(username).trim(), salt, iter: PBKDF2_ITERATIONS, iv: b64(iv), key: b64(wrapped), createdAt: new Date().toISOString() };
}

// Returns an extractable data key, or throws "Wrong user ID or password".
export async function unlockWith(auth, username, password) {
  const u = (auth?.users || []).find((x) => norm(x.username) === norm(username));
  const fail = () => {
    throw new Error("Wrong user ID or password");
  };
  if (!u) {
    // spend similar time so user IDs can't be probed by timing
    await passwordKey(username, password, b64(new Uint8Array(16)), PBKDF2_ITERATIONS).catch(() => {});
    fail();
  }
  try {
    const kek = await passwordKey(u.username, password, u.salt, u.iter || PBKDF2_ITERATIONS);
    const raw = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(u.iv) }, kek, unb64(u.key));
    return { key: await crypto.subtle.importKey("raw", raw, "AES-GCM", true, ["encrypt", "decrypt"]), user: u };
  } catch {
    fail();
  }
}

// Non-extractable copy for "remember this device" (can be stored in IndexedDB but never read out).
export async function lockedCopy(dataKey) {
  const raw = await crypto.subtle.exportKey("raw", dataKey);
  return crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]);
}

export async function encryptJSON(key, obj, extra = {}) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(JSON.stringify(obj)));
  return { encrypted: true, v: 1, app: "invoice-studio", iv: b64(iv), ct: b64(ct), ...extra };
}

export async function decryptJSON(key, env) {
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(env.iv) }, key, unb64(env.ct));
  return JSON.parse(dec.decode(pt));
}

export function passwordProblems(pw) {
  if ((pw || "").length < 8) return "Use at least 8 characters.";
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  if (kinds < 2) return "Mix letters with numbers or symbols.";
  return "";
}

// Session copy of the key (per browser tab, cleared when the tab closes or on Lock).
export async function exportKeyB64(key) {
  return b64(await crypto.subtle.exportKey("raw", key));
}
export function importKeyB64(s) {
  return crypto.subtle.importKey("raw", unb64(s), "AES-GCM", true, ["encrypt", "decrypt"]);
}
